import React, { Component, useState, useEffect, useCallback } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { wp } from "../api/WpApi";
import { PostBadge } from "../alerts/PostBadge";
import "../../styles/wpStyles.scss";
import WpService from "./WpService";
import { ItemListSchema } from "../seo/ItemListSchema";
import Breadcrumbs from "../helpers/Breadcrumbs";
import { readTime } from "../helpers/ReadTime";
import { mealPrepTime } from "../helpers/MealPrepTime";
import RecipeDataService from "../seo/RecipeDataService";

import DEFAULT_THUMBNAIL from "../../resources/not-found.png";

import data from "./_setup.json";

import PageLoading from "./PageLoading";
import MetaTags from "../seo/MetaTags";
import { Helmet } from "react-helmet";
import Pagination from "./Pagination";

// Date Format: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString

const PostList = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [postsperpage] = useState(10);
  const { page } = useParams();
  const [currentpage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [totalpages, setTotalPages] = useState(null);
  const [totalCategories, setTotalCategories] = useState(null);
  const [selectedRadio, setSelectedRadio] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc"); // default: recently added
  const [showFilter, setShowFilter] = useState(false); // Accordion state for filter (mobile only)

  const navigate = useNavigate();

  const breadcrumbsItems = [
  { name: "Home", url: "/" },
  {
    name: "All Recipes",
    url: `/${data.url.slug_posts}/`,
  },
];

  const sortOptions = [
    { value: "desc", label: "Recently Added" },
    { value: "asc", label: "Oldest" },
    { value: "az", label: "A-Z" },
    { value: "za", label: "Z-A" },
  ];

  var itemListArray = [];
  var listCount = 1;

  const getPosts = useCallback(
    (currPage, categoryId) => {
      setIsLoading(true);
      let query = wp.posts();
      if (categoryId && categoryId.length > 0) {
        const categoryIds = categoryId.map((id) => parseInt(id, 10));
        query = query.categories(categoryIds)
          .param("categories_operator", "AND");
      }
      if (sortOrder === "desc") {
        query = query.order("desc").orderby("date");
      } else if (sortOrder === "asc") {
        query = query.order("asc").orderby("date");
      } else if (sortOrder === "az") {
        query = query.order("asc").orderby("title");
      } else if (sortOrder === "za") {
        query = query.order("desc").orderby("title");
      }
      query
        .perPage(postsperpage)
        .page(currPage)
        .then((posts) => {
          setPosts(posts);
          setTotalPages(posts._paging.totalPages);
          setIsLoading(false);
          setIsFiltering(false);
        })
        .catch((error) => {
          setError(error);
          setIsLoading(false);
          setIsFiltering(false);

        });
    },
    [sortOrder, postsperpage]
  );

  useEffect(() => {
    getPosts(currentpage, selectedRadio);
    getCategories();
  }, [currentpage, selectedRadio, sortOrder, getPosts]);

  // Keep currentpage in sync with URL param
  useEffect(() => {
    const pageNum = page ? parseInt(page, 10) : 1;
    setCurrentPage(pageNum);
  }, [page]);

  const getCategories = () => {
    // Fetch pages with search value "xyz": http://wp-api.org/node-wpapi/using-the-client/#api-query-parameters
    wp.categories()
      .perPage(100)
      .then((categories) => {
        if (categories != null) {
          setTotalCategories(categories);
        }
      })
      .catch((error) => {
        setError(error);
        setIsLoading(false);
      });
  };

  const handleRadioChange = (e) => {
    let checkedRadio;
    if (e.target.checked) {
      checkedRadio = [...selectedRadio, e.target.id.toString()];
    } else {
      checkedRadio = selectedRadio.filter((f) => f !== e.target.id.toString());
    }
    setSelectedRadio(checkedRadio);
    setIsFiltering(true);
    setCurrentPage(1);
    navigate(`/${data.url.slug_posts}/`); // Reset to first page on filter change
    getPosts(1, checkedRadio);
  };

  const cbChange = (e) => {
    var cbs = document.getElementsByClassName("cb");
    for (var i = 0; i < cbs.length; i++) {
      cbs[i].checked = false;
    }
    setSelectedRadio([]);
    setIsFiltering(true);
    getPosts(1, []);
  };

  if (error) {
    return <div className="content">
      <Helmet>
        <meta name="robots" content="noindex"></meta>
      </Helmet>
      
      {posts.length == 0 ? (
        <div>
          <h3 className="h-full">Error: No Recipes found</h3>
            <a
            href="/recipes/"
            className="btn btn-secondary"
          >
            Go Back
          </a>
        </div>
      ):(
        <h3 className="h-full">Error: {error.message}</h3>
      )}
    </div>;
  } else if (isFiltering) {
    return (
      <div className="container">
        {/* <FilteringSkeleton /> */}
        <PageLoading />
      </div>
    );
  } else if (isLoading) {
    return (
      <div className="container">
        <PageLoading />
      </div>
    );
  } else if (Array.isArray(posts) && posts.length > 0) {

    return (
      <div className="content container">
        <br />
        {/* META TAGS FOR POST LIST */}
        <MetaTags
          name="description"
          content="Here you can find Every Single Andrew's Cookin' Recipe from Videos and Even More!"
        ></MetaTags>
        <MetaTags name="og_locale" content="en_US"></MetaTags>
        <MetaTags name="og_url" content={window.location.href}></MetaTags>
        <MetaTags
          name="og_site_name	"
          content="Andrew's Cookin' Recipes"
        ></MetaTags>
        <Helmet>
          <title>Recipe List, All Recipes | Andrew's Cookin'</title>
          <link
            rel="canonical"
            href={`${process.env.REACT_APP_PUBLIC_URL}/${data.url.slug_posts}/${
              currentpage > 1 ? `page-${currentpage}` : ""
            }`}
          />
        </Helmet>
        <div className="row">
          <div className="col-md"></div>
          <div className="col-md-10 grid grid-cols-1 grid-rows-2 md:grid-cols-3 md:grid-rows-1 justify-content-between align-items-center">
            <div className="md:col-start-2 md:col-end-2">
              <h1 className="fw-bold">All Recipes</h1>
              <small className="fw-light">
                Filter through to find the perfect Dish!
              </small>
            </div>
            <div className="md:col-start-3 md:col-end-3">
              <label htmlFor="sortOrder" className="me-2">
                Sort by:
              </label>
              <select
                id="sortOrder"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="form-select"
                style={{ width: "auto", display: "inline-block" }}
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* <hr /> */}
        <Breadcrumbs items={breadcrumbsItems} />
        <hr/>
        <div className="row">
          <div className="col-md">
            {/* Mobile: Accordion, Desktop: Always expanded */}
            <div className="block md:hidden">
              <div className="accordion" id="filterAccordion">
                <div className="accordion-item">
                  <h2 className="accordion-header" id="filterHeading">
                    <button
                      className={`accordion-button fw-bold flex items-center justify-between${
                        showFilter ? "" : " collapsed"
                      }`}
                      type="button"
                      onClick={() => setShowFilter((prev) => !prev)}
                      aria-expanded={showFilter}
                      aria-controls="filterCollapse"
                    >
                      <span>Filter Recipes</span>
                    </button>
                  </h2>
                  <div
                    id="filterCollapse"
                    className={`accordion-collapse collapse${
                      showFilter ? " show" : ""
                    }`}
                    aria-labelledby="filterHeading"
                    data-bs-parent="#filterAccordion"
                  >
                    <div className="accordion-body">
                      {/* ...existing filter options... */}
                      <div className="list-group-horizontal-md align-items-start filter-list-group">
                        <div className="form-check" key="default-category">
                          <input
                            key="default-category"
                            className="form-check-input"
                            type="checkbox"
                            name="checkBox-default"
                            id="default"
                            value="Default"
                            onChange={cbChange}
                            style={{ cursor: "pointer" }}
                          ></input>
                          <label
                            key="default"
                            className="form-check-label"
                            htmlFor="default"
                            style={{ cursor: "pointer" }}
                          >
                            <b>Show All Posts</b>
                          </label>
                        </div>
                        {totalCategories != null &&
                          totalCategories.map((category) => (
                            <div
                              className="form-check"
                              key={category.id + "-category"}
                            >
                              <input
                                key={category.id + "-category"}
                                className="form-check-input cb"
                                type="checkbox"
                                name={"checkBox-" + category.id}
                                id={category.id}
                                value={category.id}
                                checked={selectedRadio.includes(
                                  category.id.toString()
                                )}
                                onChange={handleRadioChange}
                                style={{ cursor: "pointer" }}
                              ></input>
                              <label
                                key={category.id}
                                className="form-check-label transition ease-in-out delay-50 duration-300"
                                htmlFor={category.id}
                                style={{ cursor: "pointer" }}
                              >
                                {category.name}
                              </label>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr />
            </div>
            {/* Desktop: Always expanded */}
            <div className="hidden md:block">
              <div className="list-group-horizontal-md align-items-start filter-list-group">
                <div className="form-check" key="default-category">
                  <input
                    key="default-category"
                    className="form-check-input"
                    type="checkbox"
                    name="checkBox-default"
                    id="default"
                    value="Default"
                    onChange={cbChange}
                    style={{ cursor: "pointer" }}
                  ></input>
                  <label
                    key="default"
                    className="form-check-label"
                    htmlFor="default"
                    style={{ cursor: "pointer" }}
                  >
                    <b>Show All Posts</b>
                  </label>
                </div>
                {totalCategories != null &&
                  totalCategories.map((category) => (
                    <div className="form-check" key={category.id + "-category"}>
                      <input
                        key={category.id + "-category"}
                        className="form-check-input cb"
                        type="checkbox"
                        name={"checkBox-" + category.id}
                        id={category.id}
                        value={category.id}
                        checked={selectedRadio.includes(category.id.toString())}
                        onChange={handleRadioChange}
                        style={{ cursor: "pointer" }}
                      ></input>
                      <label
                        key={category.id}
                        className="form-check-label transition ease-in-out delay-50 duration-300"
                        htmlFor={category.id}
                        style={{ cursor: "pointer" }}
                      >
                        {category.name}
                      </label>
                    </div>
                  ))}
              </div>
              <hr />
            </div>
          </div>
          <div className="list-group col-lg-10">
            {posts.map((post) => {
              itemListArray.push({
                "@type": "ListItem",
                position: listCount++,
                url: post.link,
              });
              const recipeData = RecipeDataService.extractRecipeData(post.content.rendered);
              return (
                <Link
                  key={post.id}
                  to={`/${data.url.slug_posts}/${post.slug}/`}
                  className="shadow-sm list-group-item list-group-item-action transition ease-in-out delay-50 duration-300 grid grid-rows-4"
                  onClick={() => {
                    sessionStorage.setItem("postListScroll", window.scrollY);
                    sessionStorage.setItem("postListPage", currentpage);
                  }}
                >
                  {(post.class_list.includes("category-recipes") ||
                    post.class_list.includes("category-recipe")) && (
                    <PostBadge
                      link={false}
                      text="Recipe"
                      style={{
                        position: "absolute",
                        top: 24,
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 20,
                      }}
                      color="success"
                      className="row-span-1"
                    />
                  )}
                  <h2
                    className="row-span-1 fw-bold"
                    style={{ padding: 20 }}
                    dangerouslySetInnerHTML={{
                      __html: post.title.rendered,
                    }}
                  />
                  <div className="row-span-1 md:grid md:grid-cols-4 gap-6 md:justify-items-between justify-items-center">
                    <div className="post-category md:order-3 md:col-span-1">
                      Categories:
                      <div>
                        {post.class_list !== null &&
                          WpService.getItemFromClassList(
                            post.class_list,
                            "category-"
                          ).map((item) => (
                            <PostBadge
                              link={false}
                              key={item}
                              text={item}
                              style="child-category"
                              color="secondary"
                            />
                          ))}
                      </div>
                    </div>
                    <div className="post-thumbnail md:order-1 md:col-span-1">
                      {post.yoast_head_json.og_image !== undefined &&
                      post.yoast_head_json.og_image ? (
                        <img
                          loading="lazy"
                          src={post.yoast_head_json.og_image[0].url}
                          alt={post.yoast_head_json.og_image[0].url}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_THUMBNAIL;
                          }}
                        ></img>
                      ) : (
                        <img
                          loading="lazy"
                          src={DEFAULT_THUMBNAIL}
                          alt="Not Found"
                        ></img>
                      )}
                    </div>

                    <p
                      className="md:order-2 md:col-span-2"
                      dangerouslySetInnerHTML={{
                        __html: post.excerpt.rendered,
                      }}
                    />
                  </div>
                  <div className="grid grid-rows-1 grid-cols-2 row-span-1 md:grid-cols-3">
                    <div className="col-span-1 col-start-2 row-span-1 row-start-1 md:col-start-1">
                      {recipeData.servings != null && (
                        <p>
                          Servings:{" "}
                          <span className="">
                            {recipeData.servings}
                          </span>
                          <span className="">
                            {" "}{recipeData.servingsUnit}
                          </span>
                        </p>
                      )}
                    </div>
                    <div className="col-span-1 col-start-1 row-span-1 row-start-1 md:col-start-2">
                      <span>
                        {/* <u>{readTime(post.content.rendered)}</u> */}
                        {(recipeData.totalTimeDays != null ||
                          recipeData.totalTimeHours != null ||
                          recipeData.totalTimeMinutes != null) ? (
                            <p>Time to make:{" "}
                              <span className="">
                                {recipeData.totalTimeDays != null && `${recipeData.totalTimeDays}` + " "}
                                {recipeData.totalTimeHours != null && `${recipeData.totalTimeHours}` + " "}
                                {recipeData.totalTimeMinutes != null && `${recipeData.totalTimeMinutes}`}
                              </span>
                        </p>
                        ) : (
                          <p className="">{readTime(post.content.rendered)}</p>
                        )}
                      </span>
                    </div>
                    
                  {(recipeData.totalTimeDays != null ||
                    recipeData.totalTimeHours != null ||
                    recipeData.totalTimeMinutes != null) ? (
                    <div className="col-span-2 row-start-2 md:row-start-1 md:col-span-1 md:col-start-3">
                      <small>
                        {new Date(post.date).toLocaleDateString(
                          data.format.date.locale,
                          data.format.date.options
                        )}
                      </small>
                    </div>
                    ) : (
                    <div className="col-start-2 col-span-1 row-start-1 md:col-span-1 md:col-start-3">
                      <small>
                        {new Date(post.date).toLocaleDateString(
                          data.format.date.locale,
                          data.format.date.options
                        )}
                      </small>
                    </div>
                    )}              
                  </div>
                </Link>
              );
            })}
            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify(ItemListSchema(itemListArray))}
              </script>
            </Helmet>
          </div>
        </div>
        {currentpage <= totalpages && (
          <div className="row">
            <div className="col-md">
              <div className="row">
                <div className="col-md d-flex justify-content-center align-items-center my-5">
                  <Pagination
                    currentPage={currentpage}
                    totalPages={totalpages}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                      navigate(
                        page === 1
                          ? `/${data.url.slug_posts}/`
                          : `/${data.url.slug_posts}/page-${page}`
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <hr />
      </div>
    );
  } else {
    return <div className="content">Nothing to View!</div>;
  }
};

export default PostList;
